import Lang from "@/lang";
import { Filter } from "@/types/filters";

export interface DateRange {
  type?: "basic" | "preset";
  value?: {
    startTime?: string;
    endTime?: string;
    preset?: string;
  };
}

export interface PlanFilterParams {
  advertiser: string[] | null;
  agency: string[] | null;
  project: string[] | null;
  scenario: string[] | null;
  plan: string[] | null;
  businessGoalPropertyValue: string[] | null;
  isApproved: string[] | null;
  search: string | null;
  dateRange: DateRange;
  lastModified: DateRange;
  projectOwnedBy: Record<string, string>[] | null;
  businessGoalOwnedBy: Record<string, string>[] | null;
  planOwnedBy: Record<string, string>[] | null;
  scenarioOwnedBy: Record<string, string>[] | null;
  page?: number;
  page_size?: number;
}

export type PlanFilterState = {
  filterParams: PlanOutputFilterParams;
  selectedFilters: string[];
};

export interface PlanOutputFilterParams {
  businessGoalPropertyValue: string[];
  mediaType: string[];
  plan: string[] | null;
  scenario: string[] | null;
}

export const defaultSelectedFilters = ["advertiser", "businessGoalPropertyValue", "isApproved", "dateRange", "search"];

export const defaultVisibleColumns = [
  "name",
  "agency_name",
  "advertiser_name",
  "owned_by",
  "business_goal",
  "approved",
  "publish_date",
  "select",
];

export const defaultTableHeaders = [
  { text: "Project - Business Goal - Plan - Scenario", value: "name" },
  { text: "Agency", value: "agency_name" },
  { text: "Advertiser", value: "advertiser_name" },
  { text: "Owned By", value: "owned_by" },
  { text: "Business Goal", value: "business_goal" },
  { text: "Is Approved", value: "approved" },
  { text: "Publish Date", value: "publish_date" },
  { text: "Last Modified", value: "last_modified" },
  { text: "Select", value: "select" },
];

export const defaultTableExportHeaders: Record<string, string> = {
  name: "Project",
  business_goal: "Business Goal",
  plan: "Plan",
  scenario: "Scenario",
  agency_name: "Agency",
  advertiser_name: "Advertiser",
  owned_by: "Owned By",
  business_goal_description: "Business Goal Description",
  approved: "Is Approved",
  publish_date: "Publish Date",
  last_modified: "Last Modified",
};

export const ALL_OBJECT = { name: "All", email: "All" };

export const defaultPlanFilterValues: PlanFilterParams = {
  advertiser: ["All"],
  agency: ["All"],
  project: ["All"],
  plan: ["All"],
  scenario: ["All"],
  businessGoalPropertyValue: ["All"],
  isApproved: ["All"],
  search: null,
  dateRange: { type: "preset", value: { preset: "allTime" } },
  lastModified: { type: "preset", value: { preset: "last12Months" } },
  projectOwnedBy: [ALL_OBJECT],
  businessGoalOwnedBy: [ALL_OBJECT],
  planOwnedBy: [ALL_OBJECT],
  scenarioOwnedBy: [ALL_OBJECT],
};

export const planListFilters: Array<Filter> = [
  {
    label: "Advertiser",
    filterName: "MultipleSelectFilter",
    filterKey: "advertiser",
    apiKey: "advertisers",
    value: null,
    items: [{ text: "All", value: null }],
    searchBar: true,
  },
  {
    label: "Business Goal",
    filterName: "MultipleSelectFilter",
    filterKey: "businessGoalPropertyValue",
    apiKey: "business_goal_property_values",
    value: null,
    isPageDetails: true,
    items: [{ text: "All", value: null }],
    searchBar: true,
  },
  {
    label: "Is Approved",
    filterName: "SingleSelectFilter",
    filterKey: "isApproved",
    apiKey: "is_approved",
    value: Lang.all,
    isPageDetails: true,
    items: [{ text: "All", value: null }],
    searchBar: true,
  },
  {
    label: "Publish Date",
    filterName: "DateRangeFilter",
    filterKey: "dateRange",
    apiKey: "dateRange",
    value: null,
    isPageDetails: true,
    default: "last12Months",
  },
  {
    label: "Last Modified",
    filterName: "DateRangeFilter",
    filterKey: "lastModified",
    apiKey: "lastModified",
    value: null,
    isPageDetails: true,
    default: "last12Months",
  },
  {
    label: "Agency",
    filterName: "MultipleSelectFilter",
    filterKey: "agency",
    apiKey: "agencies",
    value: null,
    items: [{ text: "All", value: null }],
    searchBar: true,
  },
  {
    label: "Search",
    filterName: "InputFilter",
    filterKey: "search",
    apiKey: "search",
    value: null,
    items: [{ text: "All", value: null }],
    searchBar: true,
  },
  {
    label: "Project",
    filterName: "MultipleSelectFilter",
    filterKey: "project",
    apiKey: "projects",
    value: null,
    items: [{ text: "All", value: null }],
    searchBar: true,
  },
  {
    label: "Plan",
    filterName: "MultipleSelectFilter",
    filterKey: "plan",
    apiKey: "plans",
    value: null,
    items: [{ text: "All", value: null }],
    searchBar: true,
  },
  {
    label: "Scenario",
    filterName: "MultipleSelectFilter",
    filterKey: "scenario",
    apiKey: "scenarios",
    value: null,
    items: [{ text: "All", value: null }],
    searchBar: true,
  },
  {
    label: "Project Owners",
    filterName: "MultipleObjectSelectFilter",
    filterKey: "projectOwnedBy",
    apiKey: "project_owned_by",
    value: null,
    items: [{ text: "All", value: null }],
    searchBar: true,
  },
  {
    label: "Business Goal Owners",
    filterName: "MultipleObjectSelectFilter",
    filterKey: "businessGoalOwnedBy",
    apiKey: "business_goal_owned_by",
    value: null,
    items: [{ text: "All", value: null }],
    searchBar: true,
  },
  {
    label: "Plan Owners",
    filterName: "MultipleObjectSelectFilter",
    filterKey: "planOwnedBy",
    apiKey: "plan_owned_by",
    value: null,
    items: [{ text: "All", value: null }],
    searchBar: true,
  },
  {
    label: "Scenario Owners",
    filterName: "MultipleObjectSelectFilter",
    filterKey: "scenarioOwnedBy",
    apiKey: "scenario_owned_by",
    value: null,
    items: [{ text: "All", value: null }],
    searchBar: true,
  },
];
